import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    FiArrowLeft,
    FiArrowRight,
    FiCalendar,
    FiCheck,
    FiClock,
    FiCompass,
    FiMapPin,
    FiUsers,
} from "react-icons/fi";

import "./ExperienceDetail.css";

import milliy_taom2 from "../photos/osh_tayyorlash2.jpg";
import non_yopish2 from "../photos/non_yopish2.jpg";
import ipak_yoli2 from "../photos/ipak_yuli2.jpg";
import kulolchilik2 from "../photos/kulolchilik2.jpg";
import chul_sarguzashti2 from "../photos/chul2.jpg";
import mahalliy_oila2 from "../photos/oila2.jpg";
import hunarmandchilik2 from "../photos/hunarmandchilik2.jpg";
import tog_sayohat2 from "../photos/tog2.jpg";

/* =========================================================
   EXPERIENCE DATA
   ========================================================= */

const experiences = [
    {
        id: "cooking",
        image: milliy_taom2,
        translationKey: "experienceData.cooking",
    },
    {
        id: "bread",
        image: non_yopish2,
        translationKey: "experienceData.bread",
    },
    {
        id: "silk-road",
        image: ipak_yoli2,
        translationKey: "experienceData.silkRoad",
    },
    {
        id: "pottery",
        image: kulolchilik2,
        translationKey: "experienceData.pottery",
    },
    {
        id: "desert",
        image: chul_sarguzashti2,
        translationKey: "experienceData.desert",
    },
    {
        id: "family",
        image: mahalliy_oila2,
        translationKey: "experienceData.family",
    },
    {
        id: "crafts",
        image: hunarmandchilik2,
        translationKey: "experienceData.crafts",
    },
    {
        id: "mountains",
        image: tog_sayohat2,
        translationKey: "experienceData.mountains",
    },
];

/* =========================================================
   COMPONENT
   ========================================================= */

export default function ExperienceDetail() {
    const { id } = useParams();
    const { t } = useTranslation();

    const experience = experiences.find((item) => item.id === id);

    /* =======================================================
       NOT FOUND
       ======================================================= */

    if (!experience) {
        return (
            <main className="experience-detail-page experience-not-found">
                <div className="experience-not-found-content">
                    <span>ADAM VOYAGE</span>

                    <h1>{t("experienceDetail.notFound.title")}</h1>

                    <p>{t("experienceDetail.notFound.description")}</p>

                    <Link to="/experiences">
                        <FiArrowLeft />
                        {t("experienceDetail.notFound.back")}
                    </Link>
                </div>
            </main>
        );
    }

    /* =======================================================
       TRANSLATED EXPERIENCE DATA
       ======================================================= */

    const experienceData = t(experience.translationKey, {
        returnObjects: true,
    });

    const translatedExperience =
        experienceData && typeof experienceData === "object"
            ? experienceData
            : {};

    const title = translatedExperience.title || "";
    const location = translatedExperience.location || "";
    const duration = translatedExperience.duration || "";
    const group = translatedExperience.group || "";
    const category = translatedExperience.category || "";
    const description = translatedExperience.description || "";
    const intro = translatedExperience.intro || "";
    const details = translatedExperience.details || "";

    /* =======================================================
       HIGHLIGHTS
       ======================================================= */

    const translatedHighlights = t(
        `${experience.translationKey}.highlights`,
        {
            returnObjects: true,
        }
    );

    const highlights = Array.isArray(translatedHighlights)
        ? translatedHighlights
        : Object.values(translatedHighlights || {});

    /* =======================================================
       NUMBER
       ======================================================= */

    const experienceNumber =
        experiences.findIndex((item) => item.id === experience.id) + 1;

    /* =======================================================
       RETURN
       ======================================================= */

    return (
        <>
            <Helmet>
                <title>{title} | Adam Voyage</title>

                <meta
                    name="description"
                    content={description}
                />
            </Helmet>

            <main className="experience-detail-page">

                {/* =================================================
            HERO
        ================================================= */}

                <section
                    className="experience-detail-hero"
                    style={{
                        backgroundImage: `url(${experience.image})`,
                    }}
                >
                    <div className="experience-detail-overlay" />

                    <div className="experience-detail-hero-content">

                        <Link
                            to="/experiences"
                            className="experience-back"
                        >
                            <FiArrowLeft />

                            <span>
                                {t("experienceDetail.backToExperiences")}
                            </span>
                        </Link>

                        <div className="experience-detail-category">
                            <FiCompass />

                            <span>{category}</span>
                        </div>

                        <h1>{title}</h1>

                        <p>{description}</p>

                        <div className="experience-detail-location">
                            <FiMapPin />

                            <span>{location}</span>
                        </div>

                    </div>

                    <div className="experience-detail-number">
                        {String(experienceNumber).padStart(2, "0")}
                    </div>
                </section>

                {/* =================================================
            INFO
        ================================================= */}

                <section className="experience-detail-info">
                    <div className="experience-detail-info-grid">

                        {/* Duration */}
                        <div className="experience-info-item">
                            <FiClock />

                            <div>
                                <span>
                                    {t("experienceDetail.info.duration")}
                                </span>

                                <strong>{duration}</strong>
                            </div>
                        </div>

                        {/* Group */}
                        <div className="experience-info-item">
                            <FiUsers />

                            <div>
                                <span>
                                    {t("experienceDetail.info.group")}
                                </span>

                                <strong>{group}</strong>
                            </div>
                        </div>

                        {/* Location */}
                        <div className="experience-info-item">
                            <FiMapPin />

                            <div>
                                <span>
                                    {t("experienceDetail.info.location")}
                                </span>

                                <strong>{location}</strong>
                            </div>
                        </div>

                        {/* Season */}
                        <div className="experience-info-item">
                            <FiCalendar />

                            <div>
                                <span>
                                    {t("experienceDetail.info.season")}
                                </span>

                                <strong>
                                    {t("experienceDetail.info.yearRound")}
                                </strong>
                            </div>
                        </div>

                    </div>
                </section>

                {/* =================================================
            CONTENT
        ================================================= */}

                <section className="experience-detail-content">

                    <div className="experience-detail-main">

                        <span className="experience-section-label">
                            {t("experienceDetail.aboutLabel")}
                        </span>

                        <h2>
                            {t("experienceDetail.moreThanTravel.line1")}
                            <br />

                            <strong>
                                {t("experienceDetail.moreThanTravel.line2")}
                            </strong>
                        </h2>

                        <p className="experience-detail-intro">
                            {intro}
                        </p>

                        <p className="experience-detail-intro">
                            {details}
                        </p>

                        {/* =================================================
                HIGHLIGHTS
            ================================================= */}

                        <div className="experience-highlights">

                            <h3>
                                {t("experienceDetail.highlightsTitle")}
                            </h3>

                            <div className="experience-highlights-list">

                                {highlights.map((item, index) => (
                                    <div
                                        className="experience-highlight"
                                        key={index}
                                    >
                                        <span>
                                            <FiCheck />
                                        </span>

                                        <p>{item}</p>
                                    </div>
                                ))}

                            </div>
                        </div>

                    </div>

                    {/* =================================================
              BOOKING
          ================================================= */}

                    <aside className="experience-booking-card">

                        <span>ADAM VOYAGE</span>

                        <h3>
                            {t("experienceDetail.booking.titleLine1")}
                            <br />

                            <strong>
                                {t("experienceDetail.booking.titleLine2")}
                            </strong>
                        </h3>

                        <p>
                            {t("experienceDetail.booking.description")}
                        </p>

                        <Link
                            to="/contact"
                            className="experience-booking-button"
                        >
                            <span>
                                {t("experienceDetail.booking.button")}
                            </span>

                            <FiArrowRight />
                        </Link>

                        {/* Note 1 */}
                        <div className="experience-booking-note">
                            <FiCheck />

                            <span>
                                {t("experienceDetail.booking.note1")}
                            </span>
                        </div>

                        {/* Note 2 */}
                        <div className="experience-booking-note">
                            <FiCheck />

                            <span>
                                {t("experienceDetail.booking.note2")}
                            </span>
                        </div>

                        {/* Note 3 */}
                        <div className="experience-booking-note">
                            <FiCheck />

                            <span>
                                {t("experienceDetail.booking.note3")}
                            </span>
                        </div>

                    </aside>

                </section>

                {/* =================================================
            BOTTOM
        ================================================= */}

                <section className="experience-detail-bottom">

                    <Link
                        to="/experiences"
                        className="experience-all-link"
                    >
                        <FiArrowLeft />

                        <span>
                            {t("experienceDetail.bottom.allExperiences")}
                        </span>
                    </Link>

                    <div className="experience-bottom-brand">
                        <span>ADAM</span>

                        <strong>VOYAGE</strong>
                    </div>

                </section>

            </main>
        </>
    );
}